import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { userKeys } from '../queries/keys';
import { userPaths } from '../queries/paths';

// TSS vite/03 — usuarios internos (staff) + permisos (admin-users.md). Roles:
// webmaster · admin · technician · owner. Gating: users:read (ver) · users:write
// (crear/editar, SIN rol) · users:admin (cambiar rol + desactivar). El backend aplica
// el guard de jerarquía (no asignar un rol que exceda el propio → 403).

export function useUsersList(filters = { limit: 100 }) {
    return useQuery({
        queryKey: userKeys.list(filters),
        queryFn:  ({ signal }) => api.get(userPaths.list(), { params: filters, signal }).then(r => r.data),
        placeholderData: (prev) => prev,
    });
}

// GET /:id → usuario + bloque `permissions` ({ role, superuser, grants }).
export function useUser(id) {
    return useQuery({
        queryKey: userKeys.detail(id),
        queryFn:  ({ signal }) => api.get(userPaths.detail(id), { signal }).then(r => r.data),
        enabled:  !!id,
    });
}

export function useCreateUser() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(userPaths.list(), payload).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: userKeys.all }),
    });
}

// PATCH /:id — campos no-secretos (email/name/status/remarks/externals). NO rol, NO password.
export function useUpdateUser(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(userPaths.detail(id), payload).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: userKeys.all }),
    });
}

// PATCH /:id/role — único camino de cambio de rol (users:admin + guard de jerarquía).
export function useUpdateUserRole(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (role) => api.patch(userPaths.role(id), { role }).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: userKeys.all }),
    });
}

// DELETE /:id — soft-deactivate (status=1).
export function useDeactivateUser() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api.delete(userPaths.detail(id)).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: userKeys.all }),
    });
}

// PATCH /:id { status } — usado para REACTIVAR (status:2) desde la lista (id por-llamada).
export function useSetUserStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, status }) => api.patch(userPaths.detail(id), { status }).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: userKeys.all }),
    });
}
